import { deleteRequest, get, postJson, put } from './apiClient';

/**
 * WHY A CREDIT IS GIVEN (D44) — the one list a client credit note and a vendor
 * debit note pick their reason from. Owned by the server
 * (`services/creditReason.service.js`); never build a second list here.
 *
 * `key` is what a note STORES; `label` is the only thing a person reads, and an
 * admin may change it. `noteRequired` is true for "Other". A `hidden` reason is
 * no longer offered for a new choice, but a note already carrying it keeps it.
 */
export interface CreditReason {
  key: string;
  label: string;
  sortOrder: number;
  hidden: boolean;
  builtIn: boolean;
  noteRequired: boolean;
}

export async function listCreditReasons(opts: { includeHidden?: boolean } = {}): Promise<CreditReason[]> {
  const q = opts.includeHidden ? '?includeHidden=true' : '';
  const r = await get<{ success: boolean; data: { reasons: CreditReason[] } }>(`/credit-reasons${q}`);
  return r.data?.reasons || [];
}

export async function createCreditReason(payload: { label: string }): Promise<CreditReason> {
  const r = await postJson<{ success: boolean; data: { reason: CreditReason } }>('/credit-reasons', payload);
  return r.data.reason;
}

export async function updateCreditReason(
  key: string,
  payload: { label?: string; hidden?: boolean; sortOrder?: number }
): Promise<CreditReason> {
  const r = await put<{ success: boolean; data: { reason: CreditReason } }>(
    `/credit-reasons/${encodeURIComponent(key)}`,
    payload
  );
  return r.data.reason;
}

export async function deleteCreditReason(key: string): Promise<void> {
  await deleteRequest<{ success: boolean }>(`/credit-reasons/${encodeURIComponent(key)}`);
}

/**
 * What a reason picker offers: every visible reason, plus the document's own
 * reason when an admin has since hidden it, so a saved note still shows what it
 * says. PURE.
 */
export function reasonOptions(
  reasons: CreditReason[],
  currentKey: string | null | undefined
): Array<{ value: string; label: string; noteRequired: boolean; hidden: boolean }> {
  const options = reasons
    .filter((r) => !r.hidden || r.key === currentKey)
    .map((r) => ({ value: r.key, label: r.hidden ? `${r.label} (no longer offered)` : r.label, noteRequired: r.noteRequired, hidden: r.hidden }));
  const current = String(currentKey || '').trim();
  if (current && !options.some((o) => o.value === current)) {
    // A reason deleted from the list after it was used cannot happen (the server
    // refuses), but a label must never be blank if it somehow does.
    const label = current.replace(/[_-]+/g, ' ').replace(/\b[a-z]/g, (c) => c.toUpperCase());
    options.push({ value: current, label: `${label} (not in list)`, noteRequired: false, hidden: true });
  }
  return options;
}
